<template>
  <div
    ref="aiPopup"
    v-click-outside="handleClickOutside"
    class="editor-ai-popup flex flex-col bg-background-strongestDiff mx-auto"
  >
    <div class="ai-popup-main flex flex-col pl-4 pb-4">
      <v-popover
        interactive
        tag="div"
        class="mb-5"
        content-tag="div"
        trigger="click"
        :role="'poper'"
        :on-hide="onDropdownHide"
        :on-show="onDropdownShow"
        :show-on-create="true"
      >
        <div
          ref="aiButton"
          class="flex items-center border border-solid border-important-border rounded w-fit pt box-border h-6 px-2"
          :class="{ 'usn bg-inactive-bg': loading || aiGeneratedText !== '' }"
        >
          <em
            class="custom-icons flex w-4 h-4"
            :style="getCustomIconStyle(iconPath('zap'))"
          >
          </em>
          <span class="text-sm text-text-default mx-1">{{ dropdownButtonLablel }}</span>
          <em
            class="custom-icons flex w-4 h-4"
            :style="getCustomIconStyle(iconPath(chevronIcon))"
          ></em>
        </div>
        <template #content="{ state, hide }">
          <ListDropdown
            v-if="state.isVisible"
            ref="tooltipContent"
            :items="listDropdownItems"
            :close-on-select="false"
            @click="handleItemClick($event, hide)"
            @closeEscape="hide"
          />
        </template>
      </v-popover>

      <vue-scroll
        ref="aiPopupScroll"
        class="box-border flex flex-col w-full overflow-y-hidden bg-background-strongest"
        :ops="{
          scrollPanel: { scrollingX: false },
          bar: { size: '6px', keepShow: true, gutterOfSide: '0' },
        }"
      >
        <EditorDisplay
          ref="editorDisplay"
          :content="aiGeneratedText"
          :input-place-holder="translate('assistanceWith')"
          class="z-1"
        />
      </vue-scroll>
    </div>
    <div
      class="ai-popup-footer flex flex-row border-0 border-t border-solid border-dividers-default"
    >
      <ButtonCustom
        class="text-sm font-normal rounded"
        :text="translate('copy')"
        :type="'popup-button-wrap important'"
        :disabled="!aiGeneratedText"
        :button-dimensions="{ width: 56, height: 32 }"
        @click="copyGeneratedText"
      />
      <ButtonCustom
        class="text-sm font-normal rounded ml-auto"
        :class="{ usn: !aiGeneratedText }"
        :text="translate('insertBelow')"
        :type="'popup-button-wrap transparent'"
        :button-dimensions="{ width: 100, height: 32 }"
        :disabled="!aiGeneratedText"
        @click="insertBelow"
      />
      <ButtonCustom
        class="text-sm font-normal rounded ml-1"
        :text="translate('replaceText')"
        :type="'popup-button-wrap attention'"
        :disabled="!aiGeneratedText"
        :button-dimensions="{ width: 100, height: 32 }"
        @click="replaceGeneratedText"
      />
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import ListDropdown from '@/components/widgets/ListDropdown';
import customIconMixin from '@/mixins/customIconsMixin';
import ButtonCustom from '../../elements/ButtonCustom';
import useStreamResponseReader from '@/utilities/composables/useStreamResponseReader';

const REPHRASE_TONES = ['academic', 'casual', 'confident', 'friendly', 'neutral', 'professional'];
const languages = [
  { id: 'en', labelKey: 'English' },
  { id: 'de', labelKey: 'German' },
  { id: 'fr', labelKey: 'French' },
  { id: 'es', labelKey: 'Spanish' },
  { id: 'it', labelKey: 'Italian' },
  { id: 'ja', labelKey: 'Japanese' },
  { id: 'ko', labelKey: 'Korean' },
  { id: 'pt', labelKey: 'Portuguese' },
  { id: 'zh', labelKey: 'Chinese' },
];

export default {
  name: 'AiPopup',

  components: {
    ButtonCustom,
    EditorDisplay: defineAsyncComponent(
      () => import(/* webpackChunkName: 'Editor' */ '@/components/editor/EditorDisplay')
    ),
    ListDropdown,
  },

  mixins: [customIconMixin],

  props: {
    editorId: {
      type: String,
      required: true,
    },

    originalText: {
      type: String,
      required: true,
    },

    popup: {
      type: Object,
      required: true,
    },

    component: {
      type: Object,
      required: true,
    },
  },

  emits: ['close'],

  setup() {
    const { convertStreamResponse } = useStreamResponseReader();
    return { convertStreamResponse };
  },

  data() {
    return {
      aiGeneratedText: '',
      dropdown: null,
      nextLevel: '',
      loading: false,
      command: '',
      commandType: '',
    };
  },

  computed: {
    editor() {
      return this.$store.getters.editor(this.editorId) || {};
    },

    displayEditor() {
      return this.$refs.editorDisplay.editor;
    },

    firstLevel() {
      return [
        {
          id: 'simplify',
          label: this.translate('simplify'),
          customIconName: this.iconPath('loader'),
        },
        {
          id: 'summarize',
          label: this.translate('summarize'),
          customIconName: this.iconPath('align-left'),
        },
        // {
        //   id: 'generate',
        //   label: this.translate('generate'),
        //   customIconName: this.iconPath('edit'),
        // },
        {
          id: 'rephrase',
          label: this.translate('changeTone'),
          customIconName: this.iconPath('mic'),
          rightSideIcon: this.iconPath('chevron-right'),
          items: REPHRASE_TONES.map((tone) => ({
            id: tone,
            label: this.translate(tone),
          })),
        },
        {
          id: 'translate',
          label: this.translate('translate'),
          customIconName: this.iconPath('globe'),
          rightSideIcon: this.iconPath('chevron-right'),
          items: languages.map((lang) => ({
            id: lang.id,
            label: this.translate(lang.labelKey),
          })),
        },
      ];
    },

    listDropdownItems() {
      if (this.nextLevel) {
        return this.firstLevel.find((item) => item.id === this.nextLevel).items;
      }
      return this.firstLevel;
    },

    chevronIcon() {
      return this.dropdown?.state?.isShown ? 'chevron-up' : 'chevron-down';
    },

    dropdownButtonLablel() {
      if (this.loading || this.aiGeneratedText) {
        const commandType = ` - ${this.translate(this.commandType)}`;
        const command = this.firstLevel.find((item) => item.id === this.command);
        return `${this.translate(command.label)}${this.commandType ? commandType : ''}`;
      }
      return this.translate('assistant');
    },
  },

  created() {
    window.addEventListener('keydown', this.handleEscapeClose, true);
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleEscapeClose, true);
  },

  methods: {
    handleEscapeClose(event) {
      if (event.key !== 'Escape') return;
      if (this.command && this.nextLevel) {
        const commandIndex = this.firstLevel.findIndex((item) => item.id === this.command);
        this.command = '';
        this.nextLevel = '';
        this.$refs.tooltipContent.selectedIndex = commandIndex;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (this.dropdown.state.isShown) {
        event.preventDefault();
        event.stopPropagation();
        this.handleDropdownEscape();
        return;
      }
      this.removeHighlight();
      this.handleClose(event);
    },

    handleClose(event) {
      if (this.dropdown?.state?.isShown) return;
      event?.stopPropagation();
      event?.preventDefault();
      this.component.destroy();
      this.popup.destroy();
      this.$emit('close');
    },

    handleClickOutside(event) {
      this.removeHighlight();
      this.handleClose(event);
    },

    replaceGeneratedText(event) {
      this.removeHighlight();
      this.editor.commands.unsetAllMarks();

      const { selection } = this.editor.state;
      const { from, to } = selection;
      this.editor
        .chain()
        .focus()
        .insertContentAt({ from: from - 1, to }, this.aiGeneratedText)
        .run();
      this.handleClose(event);
    },

    copyGeneratedText() {
      const contentHtml = this.displayEditor.getHTML();
      const contentPlain = this.displayEditor.getText();
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([contentHtml], { type: 'text/html' }),
        'text/plain': new Blob([contentPlain], { type: 'text/plain' }),
      });
      navigator.clipboard.write([clipboardItem]).then(() => {
        const notification = {
          message: this.translate('copiedToClipboard'),
          icon: 'copy',
          duration: 4000,
        };
        this.$store.dispatch('addUiNotification', notification);
      });
    },

    handleItemClick(item, hideFn) {
      if (this.nextLevel === '') {
        this.command = item.id;
      }
      if (item.items) {
        this.nextLevel = item.id;
        this.component.updateProps({
          props: {
            items: item.items,
          },
        });
        this.$refs.tooltipContent.selectedIndex = 0;
        this.$refs.tooltipContent.focusInput?.();
        return;
      }

      const props = {};
      switch (this.command) {
        case 'translate':
          props.lang = item.id;
          break;
        case 'rephrase':
          props.tone = item.id;
          break;
        default:
          break;
      }
      if (this.nextLevel) {
        this.commandType = item.label;
      }
      this.AiTask(props);
      if (typeof hideFn === 'function') {
        hideFn();
      }
    },

    onDropdownShow(instance) {
      if (!this.dropdown) {
        this.dropdown = instance;
      }
    },

    onDropdownHide() {
      this.nextLevel = '';
    },

    async AiTask(props) {
      this.loading = true;
      const key = this.command === 'generate' ? 'instructions' : 'input';
      const response = await this.$store.dispatch('AiTask', {
        [this.command]: {
          ...props,
          [key]: this.originalText,
        },
      });

      // pass response and should scroll
      await this.convertStreamResponse(response, true);
      this.loading = false;
    },

    insertBelow() {
      const { to } = this.editor.state.selection;
      this.removeHighlight();

      const generatedFilteredText = this.aiGeneratedText.replace(/data-block-id="[^"]*"/g, '');
      this.editor.commands.insertContentAt(to + 1, generatedFilteredText);
      this.handleClose();
    },

    removeHighlight() {
      if (Object.keys(this.editor).length) {
        this.editor.commands.removePreCommentHighlightSelection();
      }
    },

    handleDropdownEscape() {
      this.dropdown.hide();
    },

    scrollToBottom() {
      this.$refs.aiPopupScroll.scrollTo({ y: '100%' });
    },
  },
};
</script>

<style lang="scss">
.outline-none {
  .tippy-box {
    outline: none;
  }
}
.ai-popup-tippy {
  .tippy-box,
  .tippy-content {
    height: 100%;
  }
}
.editor-ai-popup {
  @apply max-w-[700px]
    w-[700px]
    rounded-lg
    shadow-md
    border
    border-solid
    border-important-border
    pt-4
    max-h-full
    relative;

  .generated-text {
    line-height: 23px !important;
  }

  .ai-popup-footer {
    padding: 8px 16px 8px 16px;
  }

  .st-editor {
    .st-editor-content {
      padding: 0 !important;
    }
  }

  .ai-popup-main {
    max-height: calc(100% - 90px);
    overflow: hidden;
  }
}
</style>
